import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Film, Sparkles, Copy, Check, Loader2, Mic, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Reel Script Generator
 * Generates AI-powered 15s voiceover scripts + shot lists for Instagram Reels
 */
export const ReelScriptGenerator = ({ spot, itineraryId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState(null);
  const [copiedVoiceover, setCopiedVoiceover] = useState(false);
  const [copiedShots, setCopiedShots] = useState(false);

  const generateScript = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/reel-script/${itineraryId}/${spot.id}`);
      setScript(res.data);
      toast.success("Reel script generated! 🎬");
    } catch (err) {
      toast.error("Failed to generate script. Try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "voiceover") {
        setCopiedVoiceover(true);
        setTimeout(() => setCopiedVoiceover(false), 2000);
      } else {
        setCopiedShots(true);
        setTimeout(() => setCopiedShots(false), 2000);
      }
      toast.success("Copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  return (
    <>
      {/* CTA Button */}
      <button
        onClick={() => {
          setIsOpen(true);
          if (!script) generateScript();
        }}
        data-testid="reel-script-cta"
        className="w-full gradient-purple-pink text-black font-display font-bold uppercase tracking-[0.2em] text-xs rounded-full py-3.5 inline-flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.99] transition"
      >
        <Film className="h-4 w-4" /> Generate Reel Script
      </button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[999]"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
              className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[90vw] sm:max-w-3xl z-[1000]"
            >
              <div className="glass-strong rounded-3xl p-6 sm:p-8 h-full sm:h-auto max-h-[90vh] overflow-y-auto ring-neon">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#A855F7] mb-2 inline-flex items-center gap-1.5">
                      <Film className="h-3.5 w-3.5" /> 15-Second Reel Script
                    </div>
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                      {spot.name}
                    </h2>
                    <p className="text-zinc-400 text-sm mt-1">{spot.city}, {spot.country}</p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-zinc-400 hover:text-white p-2 -mt-2 -mr-2"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="h-10 w-10 text-[#A855F7] animate-spin mb-4" />
                    <p className="text-zinc-400 text-sm">Crafting your reel script...</p>
                  </div>
                )}

                {/* Generated Script */}
                {!loading && script && (
                  <div className="space-y-5">
                    {/* Voiceover Section */}
                    <div className="glass rounded-2xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                          <Mic className="h-3.5 w-3.5" /> 15s Voiceover
                        </div>
                        <button
                          onClick={() => copyToClipboard(script.voiceover, "voiceover")}
                          className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white transition"
                        >
                          {copiedVoiceover ? (
                            <>
                              <Check className="h-3.5 w-3.5 text-green-400" /> Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5" /> Copy
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-zinc-100 leading-relaxed text-sm sm:text-base whitespace-pre-line">
                        {script.voiceover}
                      </p>
                    </div>

                    {/* Shot List Section */}
                    <div className="glass rounded-2xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#00F2FE]">
                          <Video className="h-3.5 w-3.5" /> Shot List
                        </div>
                        <button
                          onClick={() => copyToClipboard(script.shots.join("\n"), "shots")}
                          className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white transition"
                        >
                          {copiedShots ? (
                            <>
                              <Check className="h-3.5 w-3.5 text-green-400" /> Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5" /> Copy
                            </>
                          )}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {script.shots.map((shot, idx) => (
                          <div key={`shot-${idx}-${shot.substring(0, 20)}`} className="flex items-start gap-3">
                            <span className="shrink-0 h-6 w-6 rounded-full bg-[#00F2FE]/20 text-[#00F2FE] inline-flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <p className="text-zinc-100 text-sm leading-relaxed pt-0.5">{shot}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Regenerate Button */}
                    <button
                      onClick={generateScript}
                      disabled={loading}
                      className="w-full glass-strong hover:glass text-white font-display font-bold uppercase tracking-[0.2em] text-xs rounded-full py-3 inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      <Sparkles className="h-4 w-4" /> Regenerate Script
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
